/**
 * withFeatureFlagUpdates - Higher Order Component for automatic flag updates
 * 
 * This HOC handles feature flag updates for both class and functional components
 * without requiring manual subscription management.
 * 
 * Note: For new functional components, consider using the useFeatureFlags hook instead
 * for a more modern, hook-based approach.
 * 
 * Usage with Class Components:
 * 
 * class MyComponent extends React.Component {
 *   render() {
 *     return (
 *       <div>
 *         {this.props.isFeatureEnabled('my-flag') && <Feature />}
 *       </div>
 *     );
 *   }
 * }
 * export default withFeatureFlagUpdates(MyComponent, ['my-flag']);
 * 
 * Usage with Functional Components:
 * 
 * const MyComponent = (props) => {
 *   return (
 *     <div>
 *       {props.isFeatureEnabled('my-flag') && <Feature />}
 *     </div>
 *   );
 * };
 * export default withFeatureFlagUpdates(MyComponent, ['my-flag']);
 * 
 * Or use the hook directly (recommended for functional components):
 * 
 * import { useFeatureFlags } from './useFeatureFlags';
 * 
 * const MyComponent = () => {
 *   const { isFeatureEnabled } = useFeatureFlags(['my-flag']);
 *   return (
 *     <div>
 *       {isFeatureEnabled('my-flag') && <Feature />}
 *     </div>
 *   );
 * };
 */

import React, { Component } from 'react';
import { subscribeToFlagChanges, isFeatureEnabled, areFeaturesEnabled } from './FeatureToggleManager';

const withFeatureFlagUpdates = (WrappedComponent, watchedFlags = null) => {
  class WithFeatureFlagUpdates extends Component {
    constructor(props) {
      super(props);
      this.unsubscribe = null;
      this.state = {
        flagUpdateCounter: 0
      };
      
      // Set display name for debugging
      this.componentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    }

    componentDidMount() {
      // Subscribe to flag changes
      this.unsubscribe = subscribeToFlagChanges((changedFlags) => {
        if (watchedFlags) {
          // Only update if watched flags changed
          const relevantChanges = watchedFlags.some(flag => 
            Object.keys(changedFlags).includes(flag)
          );
          
          if (relevantChanges) {
            this.setState(prev => ({ flagUpdateCounter: prev.flagUpdateCounter + 1 }));
          }
        } else {
          // No specific flags watched, update on any change
          this.setState(prev => ({ flagUpdateCounter: prev.flagUpdateCounter + 1 }));
        }
      }, watchedFlags);
    }

    componentWillUnmount() {
      if (this.unsubscribe) {
        this.unsubscribe();
      }
    }

    render() {
      // Pass through all props plus flag helper functions
      const enhancedProps = {
        ...this.props,
        isFeatureEnabled,
        areFeaturesEnabled,
        flagUpdateCounter: this.state.flagUpdateCounter
      };

      return <WrappedComponent {...enhancedProps} />;
    }
  }

  // Set display name for React DevTools
  WithFeatureFlagUpdates.displayName = `withFeatureFlagUpdates(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  // Add debug information in development
  if (process.env.NODE_ENV === 'development') {
    WithFeatureFlagUpdates.__watchedFlags = watchedFlags;
    WithFeatureFlagUpdates.__originalComponent = WrappedComponent;
  }

  return WithFeatureFlagUpdates;
};

export default withFeatureFlagUpdates;
